import { useAction, useAtom } from "@reatom/npm-react";
import classNames from "classnames";
import {
  PriceDtoCurrencyEnum,
  ProductControllerSearchV2CurrencyEnum,
  ProductControllerSearchV2GendersEnum,
  ProductControllerSearchV2SourcesEnum,
} from "definitions/product-storage";
import { useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Options } from "react-select";
import { SelectOptionTree } from "src/components/CategorySelectTree";
import { ForwardedVirtualMultiSelect } from "src/components/VirtualMultiSelect/ForwardedVirtualMultiSelect";
import { AsyncBrandSelect } from "src/widgets/AsyncBrandSelect";

import {
  useFilterSelectMenu,
  useProductFilters,
  useProductPageTranslate,
} from "../../hooks";
import { ProductSamplesSelect } from "../ProductSamples/ProductSamplesSelect";
import { productSamplesOptionsAtom } from "../ProductSamples/store/fetchers/getProductSamplesRequest";
import { ProductSort } from "../ProductSort/ProductSort";
import {
  CreatedAtRangeInputs,
  FillProductSelectionButton,
  ForwardedCategorySelectTreeAdapterWrapper,
  PriceRangeInputs,
  ViewAllSelectedButton,
} from "./components";
import {
  currencyOptions,
  FIELDS_APPLIED_ON_BLUR,
  gendersList,
  includeCustomOptions,
  sourcesList,
} from "./ProductFilters.constants";
import css from "./ProductFilters.module.scss";
import { getCategoriesFetch } from "./store";
import { categoriesToTree } from "./utils";

export interface ProductFilters {
  search?: string;
  currency?: ProductControllerSearchV2CurrencyEnum;
  brandIds?: number[];
  categoryIds?: number[];
  priceFrom?: number;
  priceTo?: number;
  createdFrom?: Date;
  createdTo?: Date;
  viewAllSelected?: boolean;
  genders?: ProductControllerSearchV2GendersEnum[];
  sources?: ProductControllerSearchV2SourcesEnum[];
  includeCustom?: boolean;
  excludeSamples?: number[];
}

export const ProductFilters = () => {
  const { filter, updateFilters } = useProductFilters();
  const [categories] = useAtom((ctx): Options<SelectOptionTree> => {
    const categories = ctx.spy(getCategoriesFetch.dataAtom);

    if (!categories) return [];

    return categoriesToTree(categories);
  });

  const [loadingCategories] = useAtom(getCategoriesFetch.isLoadingAtom);
  const [sampleOptions] = useAtom(productSamplesOptionsAtom);
  const selectMenu = useFilterSelectMenu();

  const loadCategories = useAction(getCategoriesFetch);

  const {
    brandLabel,
    brandPlaceholder,
    currencyLabel,
    currencyPlaceholder,
    categoryLabel,
    categoryPlaceholder,
    productSamplesLabel,
    sourcesLabel,
    sourcesPlaceholder,
    includeCustomLabel,
    includeCustomPlaceholder,
    viewAllSelectedButton,
    fillProductSelectionButton,
    excludeSamplesCustomLabel,
    excludeSamplesPlaceholder,
    priceRangeLabel,
    priceRangeMinPlaceholder,
    priceRangeMaxPlaceholder,
    createdAtRangeLabel,
    createdAtRangeFromPlaceholder,
    createdAtRangeToPlaceholder,
    genderLabel,
    genderPlaceholder,
    sortLabel,
  } = useProductPageTranslate();

  const { control, reset, getValues, watch } = useForm<ProductFilters>({
    defaultValues: { currency: PriceDtoCurrencyEnum.Rub, ...filter },
  });

  const commitFilters = useCallback(() => {
    updateFilters(getValues());
  }, [getValues, updateFilters]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    reset(filter);
  }, [filter, reset]);

  useEffect(() => {
    const subscription = watch((_values, { name, type }) => {
      if (type !== "change" || !name || FIELDS_APPLIED_ON_BLUR.has(name)) {
        return;
      }

      commitFilters();
    });

    return () => subscription.unsubscribe();
  }, [watch, commitFilters]);

  return (
    <div className={css.wrapper}>
      <div className={css.formWrapper}>
        <div className={css.filterGroup}>
          <label className={css.filterLabel}>{sortLabel}</label>
          <ProductSort />
        </div>

        <div className={css.filterGroup}>
          <label className={css.filterLabel}>{categoryLabel}</label>
          <Controller
            name="categoryIds"
            control={control}
            render={({ field }) => (
              <ForwardedCategorySelectTreeAdapterWrapper
                isMulti
                placeholder={categoryPlaceholder}
                isLoading={loadingCategories}
                {...field}
                options={categories}
              />
            )}
          />
        </div>

        <div className={css.filterGroup}>
          <label className={css.filterLabel}>{currencyLabel}</label>
          <Controller
            name="currency"
            control={control}
            render={({ field }) => {
              return (
                <ForwardedVirtualMultiSelect
                  {...selectMenu}
                  isMulti={false}
                  options={currencyOptions}
                  {...field}
                  placeholder={currencyPlaceholder}
                />
              );
            }}
          />
        </div>
        <div className={css.filterGroup}>
          <label className={css.filterLabel}>
            {priceRangeLabel}, {getValues("currency")}
          </label>
          <PriceRangeInputs
            control={control}
            minPlaceholder={priceRangeMinPlaceholder}
            maxPlaceholder={priceRangeMaxPlaceholder}
            onCommit={commitFilters}
          />
        </div>
        <div className={css.filterGroup}>
          <label className={css.filterLabel}>{createdAtRangeLabel}</label>
          <CreatedAtRangeInputs
            control={control}
            fromPlaceholder={createdAtRangeFromPlaceholder}
            toPlaceholder={createdAtRangeToPlaceholder}
            onCommit={commitFilters}
          />
        </div>
        <div className={css.filterGroup}>
          <label className={css.filterLabel}>{brandLabel}</label>
          <Controller
            name="brandIds"
            control={control}
            render={({ field }) => {
              return (
                <AsyncBrandSelect
                  {...field}
                  {...selectMenu}
                  placeholder={brandPlaceholder}
                />
              );
            }}
          />
        </div>

        <div className={css.filterGroup}>
          <label className={css.filterLabel}>{genderLabel}</label>
          <Controller
            name="genders"
            control={control}
            render={({ field }) => {
              return (
                <ForwardedVirtualMultiSelect
                  {...selectMenu}
                  isMulti={true}
                  options={gendersList}
                  {...field}
                  placeholder={genderPlaceholder}
                />
              );
            }}
          />
        </div>

        <div className={css.filterGroup}>
          <label className={css.filterLabel}>{sourcesLabel}</label>
          <Controller
            name="sources"
            control={control}
            render={({ field }) => {
              return (
                <ForwardedVirtualMultiSelect
                  {...selectMenu}
                  isMulti={true}
                  options={sourcesList}
                  {...field}
                  placeholder={sourcesPlaceholder}
                />
              );
            }}
          />
        </div>

        <div className={css.filterGroup}>
          <label className={css.filterLabel}>{includeCustomLabel}</label>
          <Controller
            name="includeCustom"
            control={control}
            render={({ field }) => {
              return (
                <ForwardedVirtualMultiSelect
                  {...selectMenu}
                  isMulti={false}
                  options={includeCustomOptions}
                  {...field}
                  placeholder={includeCustomPlaceholder}
                />
              );
            }}
          />
        </div>

        <div className={css.filterGroup}>
          <label className={css.filterLabel}>{excludeSamplesCustomLabel}</label>
          <Controller
            name="excludeSamples"
            control={control}
            render={({ field }) => {
              return (
                <ForwardedVirtualMultiSelect
                  {...selectMenu}
                  isMulti
                  options={sampleOptions}
                  {...field}
                  placeholder={excludeSamplesPlaceholder}
                />
              );
            }}
          />
        </div>

        <div className={classNames(css.filterGroup, css.samplesGroup)}>
          <label className={css.filterLabel}>{productSamplesLabel}</label>
          <ProductSamplesSelect />
          <FillProductSelectionButton buttonText={fillProductSelectionButton} />
          <ViewAllSelectedButton
            control={control}
            buttonText={viewAllSelectedButton}
          />
        </div>
      </div>
    </div>
  );
};
